import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resolveShareToken, installTool, installToolForAgency, type ResolvedShare } from '../../services/myToolsApi';
import McpDetailBody from '../../components/McpDetailBody';
import ShareResolveDetailView from '../../components/ShareResolveDetailView';
import { ROUTES } from '../../app/routes';
import '../../styles/MyTools.css';
type Status = 'loading' | 'preview' | 'installing' | 'installed' | 'error';
const TOOL_TYPE_LABELS: Record<ResolvedShare['toolType'], string> = {
    skill: 'Skill', agent: 'Agent', workflow: 'Workflow', widget: 'Widget', mcp_server: 'MCP Server', integration: 'Integration',
};
const ShareResolvePage: React.FC = () => {
    const { token } = useParams<{
        token: string;
    }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('loading');
    const [error, setError] = useState('');
    const [resolved, setResolved] = useState<ResolvedShare | null>(null);
    const [installingForAgency, setInstallingForAgency] = useState(false);
    const [agencyInstalled, setAgencyInstalled] = useState(false);
    const [trusted, setTrusted] = useState(false);
    // Loading -> Preview only resolves the share (minting/looking up the
    // independent copy server-side) and shows what it contains — nothing is
    // installed onto this account yet. Install only happens once the user
    // confirms via handleConfirm.
    const load = useCallback(async () => {
        if (!token)
            return;
        setStatus('loading');
        setError('');
        try {
            const share = await resolveShareToken(token);
            setResolved(share);
            setStatus('preview');
        }
        catch (err: any) {
            setError(err.message || 'This link could not be opened.');
            setStatus('error');
        }
    }, [token]);
    useEffect(() => { load(); }, [load]);
    const handleConfirm = async () => {
        if (!resolved)
            return;
        setStatus('installing');
        setError('');
        try {
            await installTool(resolved.copiedToolId, undefined, 'share_link');
        }
        catch (err: any) {
            if (err.message !== 'already_installed') {
                setError(err.message || 'This link could not be opened.');
                setStatus('error');
                return;
            }
        }
        setStatus('installed');
    };
    const handleInstallForAgency = async () => {
        if (!resolved)
            return;
        setInstallingForAgency(true);
        try {
            await installToolForAgency(resolved.copiedToolId);
            setAgencyInstalled(true);
        }
        catch (err: any) {
            setError(err.message || 'Failed to install for your agency.');
        }
        finally {
            setInstallingForAgency(false);
        }
    };
    if (status === 'loading' || status === 'installing') {
        return (<div className="mt-page">
        <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '60vh', textAlign: 'center', gap: 14,
            }}>
          <div style={{ fontSize: 32 }}>⏳</div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {status === 'loading' ? 'Opening shared link…' : 'Installing…'}
          </p>
        </div>
      </div>);
    }
    if (status === 'preview' && resolved) {
        return (<div className="mt-page">
        <ShareResolveDetailView icon={resolved.toolIcon || '📦'} name={resolved.toolName} typeLabel={TOOL_TYPE_LABELS[resolved.toolType]} category={resolved.toolCategory} description={resolved.toolDescription}>
          {resolved.toolType === 'mcp_server' && (<>
            <h4 className="mt-detail-section-title">MCP Server</h4>
            <McpDetailBody url={resolved.mcpUrl} headers={resolved.mcpHeaderKeys.map(key => ({ key }))}/>
          </>)}
        </ShareResolveDetailView>

        <div className="mt-detail-card" style={{ marginTop: 14, marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={trusted} onChange={e => setTrusted(e.target.checked)} style={{ marginTop: 2 }}/>
            I trust this community shared item and understand it comes from another agency in the Momentum AMP community, not from Momentum AMP directly.
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="mt-btn mt-btn--primary" disabled={!trusted} onClick={handleConfirm}>
              Install
            </button>
          </div>
        </div>
      </div>);
    }
    return (<div className="mt-page">
      <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            minHeight: '60vh', textAlign: 'center', gap: 14,
        }}>
        {status === 'installed' && resolved ? (<>
            <div style={{ fontSize: 32 }}>✅</div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>
              "{resolved.toolName}" installed
            </h2>
            {resolved.toolDescription && (<p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{resolved.toolDescription}</p>)}
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              {resolved.isAgencyOwner && !agencyInstalled && (<button type="button" className="mt-btn mt-btn--secondary" onClick={handleInstallForAgency} disabled={installingForAgency}>
                  {installingForAgency ? 'Installing…' : 'Install for Agency'}
                </button>)}
              {agencyInstalled && <span style={{ color: 'var(--text-secondary)', fontSize: 13, alignSelf: 'center' }}>Installed for your agency ✓</span>}
              <button type="button" className="mt-btn mt-btn--primary" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=installed`)}>
                Go to My Tools
              </button>
            </div>
          </>) : (<>
            <div style={{ fontSize: 32 }}>⚠️</div>
            <h2 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>Couldn't open this link</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 420 }}>{error}</p>
          </>)}
      </div>
    </div>);
};
export default ShareResolvePage;
